import { useState } from 'react';
import { FormEvent } from 'react';
import SEO from '../../components/SEO';

export default function ConsultationPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    budget: '',
    timeline: '',
    services: [] as string[],
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const serviceOptions = [
    '一頁式網站建置',
    '多頁式官網設計',
    '電商網站開發',
    'SEO優化服務',
    '年度維護管理',
    '品牌視覺設計',
    '網站功能擴充',
    '其他客製化需求'
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "免費諮詢申請",
    "description": "填寫表單申請免費網站建置諮詢服務，專業團隊將在24小時內與您聯繫，為您提供最適合的網站解決方案",
    "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/consultation`,
    "mainEntity": {
      "@type": "Organization",
      "name": "行曜科技",
      "url": import.meta.env.VITE_SITE_URL || "https://example.com",
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "email": "server@xingyaotech.tw",
        "availableLanguage": ["Chinese"],
        "areaServed": "TW",
        "hoursAvailable": {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": "09:00",
          "closes": "18:00"
        }
      }
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // 直接發送到 Supabase 並觸發郵件通知（不再向 Readdy 表單系統提交）
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
      
      const response = await fetch(`${supabaseUrl}/functions/v1/send-consultation-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
          'apikey': supabaseKey,
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          budget: formData.budget,
          timeline: formData.timeline,
          services: formData.services.join(', '),
          message: formData.message,
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ 
          name: '', email: '', phone: '', company: '', budget: '', 
          timeline: '', services: [], message: '' 
        });
      } else {
        console.error('Supabase response error:', await response.text());
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('提交錯誤:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (service: string) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-green-50">
      <SEO
        title="免費諮詢申請 | 專業網站建置諮詢服務"
        description="填寫表單申請免費網站建置諮詢服務，我們的專業團隊將在24小時內與您聯繫，為您提供最適合的網站解決方案。包含一頁式網站、官網設計、電商開發等服務。"
        keywords="免費諮詢,網站建置,諮詢申請,網站設計,官網建置,電商開發,專業服務"
        canonical="/consultation"
        schemaData={schemaData}
      />
      
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center">
                <i className="ri-global-line text-white text-xl"></i>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">網站建置服務</h1>
                <p className="text-sm text-gray-600">專業網站解決方案</p>
              </div>
            </div>
            <button 
              onClick={() => window.history.back()}
              className="px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              返回
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* 頁面標題 */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-gradient-to-r from-teal-500 to-green-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <i className="ri-customer-service-2-line text-white text-3xl"></i>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">免費諮詢申請</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            填寫以下表單，我們的專業團隊將在24小時內與您聯繫，為您提供最適合的網站解決方案
          </p>
        </div>

        {/* 表單區塊 */}
        <div className="bg-white rounded-3xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-teal-500 to-green-500 px-8 py-6">
            <h2 className="text-2xl font-bold text-white flex items-center">
              <i className="ri-file-list-3-line mr-3"></i>
              諮詢資訊表單
            </h2>
            <p className="text-teal-50 mt-2">請詳細填寫以下資訊，幫助我們更好地了解您的需求</p>
          </div>

          <form onSubmit={handleSubmit} data-readdy-form className="p-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* 左側欄位 */}
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    聯絡人姓名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="請輸入您的姓名"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    電子郵件 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="example@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    聯絡電話 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="09XX-XXX-XXX"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                    公司/品牌名稱
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="選填"
                  />
                </div>
              </div>

              {/* 右側欄位 */}
              <div className="space-y-6">
                <div>
                  <label htmlFor="budget" className="block text-sm font-semibold text-gray-700 mb-2">
                    預算範圍
                  </label>
                  <select
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  >
                    <option value="">請選擇預算範圍</option>
                    <option value="10萬以下">10萬以下</option>
                    <option value="10-20萬">10-20萬</option>
                    <option value="20-50萬">20-50萬</option>
                    <option value="50萬以上">50萬以上</option>
                    <option value="待討論">待討論</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="timeline" className="block text-sm font-semibold text-gray-700 mb-2">
                    預計完成時間
                  </label>
                  <select
                    id="timeline"
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  >
                    <option value="">請選擇時間範圍</option>
                    <option value="1個月內">1個月內</option>
                    <option value="2-3個月">2-3個月</option>
                    <option value="3-6個月">3-6個月</option>
                    <option value="6個月以上">6個月以上</option>
                    <option value="彈性安排">彈性安排</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    需要的服務項目 <span className="text-gray-500">(可多選)</span>
                  </label>
                  <div className="grid grid-cols-1 gap-2">
                    {serviceOptions.map((service) => (
                      <label key={service} className="flex items-center p-2 hover:bg-gray-50 rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.services.includes(service)}
                          onChange={() => handleServiceChange(service)}
                          className="w-4 h-4 text-teal-500 border-gray-300 rounded focus:ring-teal-500"
                        />
                        <span className="ml-3 text-sm text-gray-700">{service}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* 詳細需求說明 */}
            <div className="mt-8">
              <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                詳細需求說明
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={6}
                maxLength={500}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                placeholder="請詳細說明您的網站需求、目標客群、預期功能等（限500字）"
              />
              <div className="text-xs text-gray-500 mt-1 text-right">
                {formData.message.length}/500
              </div>
            </div>

            {/* 提交狀態訊息 */}
            {submitStatus === 'success' && (
              <div className="mt-8 p-6 bg-green-50 border border-green-200 rounded-lg flex items-start">
                <i className="ri-checkbox-circle-line text-green-600 text-2xl mr-4 mt-1"></i>
                <div>
                  <h3 className="text-green-800 font-semibold mb-1">提交成功！</h3>
                  <p className="text-green-700 text-sm">
                    感謝您的申請，我們已收到您的諮詢需求。專業顧問將在24小時內主動與您聯繫，為您提供最適合的網站解決方案。
                  </p>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mt-8 p-6 bg-red-50 border border-red-200 rounded-lg flex items-start">
                <i className="ri-error-warning-line text-red-600 text-2xl mr-4 mt-1"></i>
                <div>
                  <h3 className="text-red-800 font-semibold mb-1">提交失敗</h3>
                  <p className="text-red-700 text-sm">
                    很抱歉，系統暫時無法處理您的申請。請稍後再試，或直接撥打客服電話與我們聯繫。
                  </p>
                </div>
              </div>
            )}

            {/* 提交按鈕 */}
            <div className="mt-8 flex justify-center gap-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-8 py-4 bg-gradient-to-r from-teal-500 to-green-500 text-white font-semibold rounded-lg hover:from-teal-600 hover:to-green-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-lg whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-2-line animate-spin mr-2"></i>
                    提交中...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-2-line mr-2"></i>
                    送出諮詢申請
                  </>
                )}
              </button>
              
              <a
                href="https://line.me/R/ti/p/@313xgvjh?ts=01112329&oat_content=url"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-all text-lg whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl flex items-center"
              >
                <i className="ri-chat-1-fill mr-2"></i>
                加入官方 LINE
              </a>
            </div>
          </form>
        </div>

        {/* 聯絡資訊 */}
        <div className="mt-12 text-center">
          <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">其他聯絡方式</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-3">
                  <i className="ri-mail-line text-teal-600 text-xl"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-1">電子郵件</h4>
                <p className="text-gray-600 text-sm">server@xingyaotech.tw</p>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-3">
                  <i className="ri-time-line text-teal-600 text-xl"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-1">服務時間</h4>
                <p className="text-gray-600 text-sm">週一至週五 9:00-18:00</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
